import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import {
  getIllustrationDetails,
  checkVpnConnection,
  getDarkroomStorageUrls,
  uploadFileToBucket,
  syncBcsIllustrationUrls,
  updateDefaultIllustrationSynced,
  updateDefaultIllustrationLight,
  updateDefaultIllustrationDark,
} from "../api/illustrationsApi";
import AssetSelector from "../../common/components/AssetSelector";
import styles from "./UpdateDefaultIllustrationPage.module.css";
import { getRasterImageFromSvg } from "../utils/illustrationTransformationUtils";

const VPN_ENVIRONMENT = process.env.REACT_APP_VPN_ENVIRONMENT;
const IS_INTEGRATION = process.env.REACT_APP_ENVIRONMENT === 'integration';
const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === 'prod';

function getBffUrl(env) {
  if (env === 'prod') return process.env.REACT_APP_BFF_BASE_URL;
  if (env === 'qa') {
    return (
      process.env.REACT_APP_QA_BFF_BASE_URL ||
      process.env.REACT_APP_INT_BFF_BASE_URL ||
      process.env.REACT_APP_BFF_BASE_URL
    );
  }
  return process.env.REACT_APP_INT_BFF_BASE_URL || process.env.REACT_APP_BFF_BASE_URL;
}

const texts = {
  breadcrumbAssets: "assets",
  breadcrumbUpdateIllustration: "update default illustration",
  subHeading: "Edit Default Illustration",
  vpnRequiredTitle: `${VPN_ENVIRONMENT} VPN required`,
  vpnRequiredContent: `You need to be connected to the ${VPN_ENVIRONMENT} VPN to upload or update default illustrations.`,
  productCategoryLabel: "Product Category",
  illustrationCategoryLabel: "Illustration Category",
  illustrationNameLabel: "Illustration Name",
  illustrationNamePlaceholder: "Illustration Name",
  lightLabel: "Light",
  darkLabel: "Dark",
  syncBothLabel: "Sync both variants",
  syncBothHelperChecked: "Selected file will apply to both light and dark",
  syncBothHelperUnchecked: "Uncheck to upload a separate dark variant",
  currentLightImageLabel: "Current light image",
  currentDarkImageLabel: "Current dark image",
  saveButton: "Save",
  cancelButton: "Cancel",
  fileEmptyError: "A file must be selected",
  atLeastOneFileError: "Select at least one file to update",
  vpnErrorTitle: "VPN connection error",
  vpnErrorContent: `Please connect to ${VPN_ENVIRONMENT} VPN`,
  vpnErrorLog: "VPN connection error. Please connect to VPN",
  updateFailedTitle: "Update failed",
  darkroomUploadFailedContent:
    "Darkroom upload failed, so the illustration was not updated. Please try again. If the problem persists, log out, log back in and retry.",
  illustrationUpdatedTitle: "Illustration updated",
  illustrationUpdatedContent: (combinedName) =>
    `Default illustration for ${combinedName} has been updated successfully`,
  illustrationUpdatedBcsSyncFailedContent: (combinedName) =>
    `Default illustration for ${combinedName} was updated, but BCS URLs could not be refreshed. Open BCS Config and re-save.`,
};

function UpdateDefaultIllustrationPage() {
  const history = useHistory();
  const canvasRef = useRef(null);
  const { user } = useContext(UserContext);
  const { id } = useParams();

  // Form field states
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const [selectedIllustrationCategory, setSelectedIllustrationCategory] = useState("");
  const [illustrationName, setIllustrationName] = useState(null);

  // Variant sync state
  const [variantSync, setVariantSync] = useState(true);

  // File states
  const [illustrationFile, setIllustrationFile] = useState(null);
  const [illustrationFileDark, setIllustrationFileDark] = useState(null);

  // Error states
  const [productCategoryDropdownError, setProductCategoryDropdownError] = useState(null);
  const [illustrationCategoryDropdownError, setIllustrationCategoryDropdownError] = useState(null);
  const [illustrationNameError, setIllustrationNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);
  const [assetSelectorDarkError, setAssetSelectorDarkError] = useState(null);

  // Illustration data + page state
  const [illustrationDetailsObj, setIllustrationDetailsObj] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const handleCancelClick = () => history.goBack();

  const uploadToDarkroom = async (canvas, svgFile, darkroomName) => {
    const blob = await getRasterImageFromSvg(svgFile, canvas);
    const darkroomResponse = await getDarkroomStorageUrls(darkroomName);
    await uploadFileToBucket(
      darkroomResponse.data.data.upload_url,
      blob,
      user.idToken
    );
    return darkroomResponse;
  };

  const checkUserVpnConnection = async () => {
    try {
      const isConnected = await checkVpnConnection();
      if (!isConnected) {
        setIsLoading(false);
        setNotification({
          title: texts.vpnErrorTitle,
          content: texts.vpnErrorContent,
          isSuccess: false,
          isDanger: true,
        });
      }
      return isConnected;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleSaveClick = async () => {
    // Validation
    if (variantSync) {
      if (!illustrationFile) {
        setAssetSelectorError(texts.fileEmptyError);
        return;
      }
    } else {
      if (!illustrationFile && !illustrationFileDark) {
        setAssetSelectorError(texts.atLeastOneFileError);
        return;
      }
    }

    const canvas = canvasRef.current;
    const combinedName = illustrationDetailsObj.combined_name;
    setIsLoading(true);
    setNotification(null);

    try {
      const isConnected = await checkUserVpnConnection();
      if (!isConnected) return;

      let lightUrl = null;
      let darkroomFailed = false;

      if (variantSync) {
        // Path 1: sync=true — one file, both variants
        let darkroomResponse;
        try {
          darkroomResponse = await uploadToDarkroom(canvas, illustrationFile, combinedName);
        } catch (error) {
          console.error(error);
          setIsLoading(false);
          setNotification({ title: texts.updateFailedTitle, content: texts.darkroomUploadFailedContent, isSuccess: false, isDanger: true });
          return;
        }
        lightUrl = darkroomResponse.data.data.public_url;
        await updateDefaultIllustrationSynced(user.uid, id, illustrationFile, combinedName, lightUrl);
      } else {
        // Path 2/3/both: update whichever files were selected
        if (illustrationFile) {
          let resp;
          try {
            resp = await uploadToDarkroom(canvas, illustrationFile, combinedName);
          } catch (error) {
            console.error(error);
            darkroomFailed = true;
          }
          if (resp) {
            lightUrl = resp.data.data.public_url;
            await updateDefaultIllustrationLight(user.uid, id, illustrationFile, combinedName, lightUrl);
          }
        }

        if (illustrationFileDark) {
          let resp;
          try {
            resp = await uploadToDarkroom(canvas, illustrationFileDark, "dark_" + combinedName);
          } catch (error) {
            console.error(error);
            darkroomFailed = true;
          }
          if (resp) {
            await updateDefaultIllustrationDark(user.uid, id, illustrationFileDark, combinedName, resp.data.data.public_url);
          }
        }

        if (darkroomFailed && !lightUrl && !illustrationFileDark) {
          setIsLoading(false);
          setNotification({ title: texts.updateFailedTitle, content: texts.darkroomUploadFailedContent, isSuccess: false, isDanger: true });
          return;
        }
      }

      // BCS sync — only when light URL changed
      let bcsSyncFailed = false;
      if (lightUrl) {
        try {
          const configKey = combinedName.toUpperCase();
          const darkUrl = variantSync
            ? lightUrl
            : (illustrationDetailsObj.default.darkroom_public_url_dark ?? lightUrl);
          const envsToSync = IS_PRODUCTION
            ? [
                { bffBaseUrl: getBffUrl('integration'), bcsEnv: 'integration' },
                { bffBaseUrl: getBffUrl('qa'), bcsEnv: 'qa' },
                { bffBaseUrl: getBffUrl('prod'), bcsEnv: undefined },
              ]
            : IS_INTEGRATION
            ? [
                { bffBaseUrl: getBffUrl('integration'), bcsEnv: 'integration' },
                { bffBaseUrl: getBffUrl('qa'), bcsEnv: 'qa' },
              ]
            : [{ bffBaseUrl: undefined, bcsEnv: undefined }];

          await Promise.all(
            envsToSync.map(({ bffBaseUrl, bcsEnv }) =>
              syncBcsIllustrationUrls(configKey, lightUrl, darkUrl, user.idToken, bffBaseUrl, bcsEnv)
            )
          );
        } catch (error) {
          console.error("BCS sync failed:", error);
          bcsSyncFailed = true;
        }
      }

      setIsLoading(false);
      history.push(`/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${id}`, {
        notification: {
          title: texts.illustrationUpdatedTitle,
          content: bcsSyncFailed
            ? texts.illustrationUpdatedBcsSyncFailedContent(combinedName)
            : texts.illustrationUpdatedContent(combinedName),
          isSuccess: true,
          isDanger: false,
        },
      });
    } catch (error) {
      setIsLoading(false);
      if (error.name === "FirebaseError") {
        console.error(error.message);
      } else if (error.message === "Illustration with the same name already exists") {
        setIllustrationNameError(error.message);
      } else {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    getIllustrationDetails(id)
      .then((doc) => {
        setIllustrationDetailsObj(doc);
        setSelectedProductCategory({ id: "0", key: doc.name.product_category });
        setSelectedIllustrationCategory({ id: "0", key: doc.name.illustration_category });
        setIllustrationName(doc.name.illustration_name);
        setVariantSync(doc.variant_sync !== false);
        setIsLoading(false);
      })
      .catch((error) => console.error(error));
  }, [id]);

  const lightImgUrl = illustrationDetailsObj?.default?.img_url;
  const darkImgUrl = illustrationDetailsObj?.default?.img_url_dark ?? lightImgUrl;

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success={notification ? notification.isSuccess : false}
                danger={notification ? notification.isDanger : false}
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => setNotification(null)}
              />
            </div>
          </div>
          <div className={styles.updatePageContainer}>
            <canvas style={{ display: "none" }} ref={canvasRef} />
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb text={texts.breadcrumbAssets} tagProps={{ to: "/" }} as={Link} />
                <Separator />
                <Crumb
                  text={illustrationDetailsObj.combined_name}
                  tagProps={{ to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${id}` }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text={texts.breadcrumbUpdateIllustration}
                  tagProps={{ to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${id}/${routes.UPDATE}` }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading}>{illustrationDetailsObj.combined_name}</h3>
              <h5 className={styles.subHeading}>{texts.subHeading}</h5>
              <div className={styles.flagContainer}>
                <Flag page info title={texts.vpnRequiredTitle} content={texts.vpnRequiredContent} />
              </div>
              <div className={styles.formRow}>
                <div className={styles.productCategoryContainer}>
                  <p className={styles.inputLabel}>{texts.productCategoryLabel}</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedProductCategory]}
                      onChange={(v) => { setSelectedProductCategory(v); setProductCategoryDropdownError(null); }}
                      stretch invalid={!!productCategoryDropdownError} disabled={true}
                      initialSelectedItem={selectedProductCategory}
                    />
                  </div>
                  {productCategoryDropdownError ? <Flag title={productCategoryDropdownError} /> : null}
                </div>
                <div className={styles.illustrationCategoryContainer}>
                  <p className={styles.inputLabel}>{texts.illustrationCategoryLabel}</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedIllustrationCategory]}
                      onChange={(v) => { setSelectedIllustrationCategory(v); setIllustrationCategoryDropdownError(null); }}
                      invalid={!!illustrationCategoryDropdownError} stretch disabled={true}
                      initialSelectedItem={selectedIllustrationCategory}
                    />
                  </div>
                  {illustrationCategoryDropdownError ? <Flag title={illustrationCategoryDropdownError} /> : null}
                </div>
                <div className={styles.illustrationNameContainer}>
                  <p className={styles.inputLabel}>{texts.illustrationNameLabel}</p>
                  <div className={styles.inputContainer}>
                    <TextField
                      required={!!illustrationNameError} size="m"
                      placeholder={texts.illustrationNamePlaceholder}
                      type="text" disabled={true} stretch
                      onChange={(e) => { setIllustrationName(e.target.value); setIllustrationNameError(null); }}
                      pattern="[a-z\d_]+" value={illustrationName}
                    />
                  </div>
                  {illustrationNameError ? <Flag title={illustrationNameError} /> : null}
                </div>
              </div>

              {/* Sync both variants checkbox */}
              <label className={styles.variantSyncRow}>
                <input
                  type="checkbox"
                  checked={variantSync}
                  onChange={(e) => {
                    setVariantSync(e.target.checked);
                    if (e.target.checked) {
                      setIllustrationFileDark(null);
                      setAssetSelectorDarkError(null);
                    }
                  }}
                />
                <div className={styles.variantSyncText}>
                  <span className={styles.variantSyncLabel}>{texts.syncBothLabel}</span>
                  <span className={styles.variantSyncHelper}>
                    {variantSync ? texts.syncBothHelperChecked : texts.syncBothHelperUnchecked}
                  </span>
                </div>
              </label>

              {/* Illustration selector(s) */}
              <div className={styles.illustrationSelectorContainer}>
                {/* Light selector — always shown */}
                <div className={styles.inputContainer}>
                  <AssetSelector
                    heading={
                      <span>
                        {texts.currentLightImageLabel}{" "}
                        <span className={styles.modeChip}>{texts.lightLabel}</span>
                      </span>
                    }
                    asset={lightImgUrl}
                    onAssetSelected={(file) => {
                      if (file) { setIllustrationFile(file); setAssetSelectorError(null); }
                    }}
                  />
                </div>
                {assetSelectorError ? <Flag error title={assetSelectorError} /> : null}

                {/* Dark selector — shown only when sync is off */}
                {!variantSync && (
                  <div className={styles.inputContainer} style={{ marginTop: 24 }}>
                    <AssetSelector
                      heading={
                        <span>
                          {texts.currentDarkImageLabel}{" "}
                          <span className={styles.modeChipDark}>{texts.darkLabel}</span>
                        </span>
                      }
                      asset={darkImgUrl}
                      darkBackground
                      onAssetSelected={(file) => {
                        if (file) { setIllustrationFileDark(file); setAssetSelectorDarkError(null); }
                      }}
                    />
                  </div>
                )}
                {assetSelectorDarkError ? <Flag error title={assetSelectorDarkError} /> : null}
              </div>

              <Stack>
                <Button primary onClick={handleSaveClick}>{texts.saveButton}</Button>
                <Button secondary onClick={handleCancelClick}>{texts.cancelButton}</Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateDefaultIllustrationPage;
